import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('section:morph', this.#handleSectionMorph);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('section:morph', this.#handleSectionMorph);
  }

  /**
   * After any section morph, re-inflate the template if the server response
   * replaced our dialog content with a fresh <template> wrapper.
   */
  #handleSectionMorph = () => {
    this.#inflateTemplate();
  };

  /**
   * Inflate the <template> content into the dialog.
   * This avoids parsing ~282 DOM elements on page load.
   * Runs on every open/cart-add because section re-rendering can
   * replace the dialog innerHTML with a fresh server response that
   * still contains the <template> wrapper.
   *
   * Classic <script src> tags cloned out of a <template> behave
   * inconsistently across engines:
   *   - older WebKit set the "already started" flag on the clone, so
   *     scripts never fired and custom elements never registered
   *     (qty/remove silently no-op'd — the May '26 Safari fix)
   *   - current WebKit (26+) and Chrome DO fire them on insertion,
   *     which means a classic script that declares a top-level
   *     const/let crashes on every re-inflate (section morph → here)
   *     with "Can't create duplicate variable …" and aborts.
   *
   * Approach that works across all engines: pull src scripts out of the
   * cloned fragment BEFORE insertion (no auto-execute possible), then for
   * each URL we haven't loaded yet — exactly once per drawer lifetime —
   * load module scripts via dynamic import() (a freshly-injected
   * <script type="module"> is inserted-but-never-evaluated on Safari < 26)
   * and inject classic scripts as a fresh <script>.
   */
  #inflateTemplate() {
    const dialog = this.querySelector('dialog');
    const template = dialog?.querySelector('template');
    if (!dialog || !template) return;
    const fragment = template.content.cloneNode(true);

    // Detach all <script src> from the fragment before insertion. Modules
    // already-loaded would no-op, but classic scripts (cart-discount-
    // celebration.js) re-execute and crash on duplicate top-level consts.
    const scripts = Array.from(fragment.querySelectorAll('script[src]'));
    scripts.forEach((s) => s.remove());

    dialog.appendChild(fragment);
    template.remove();

    // Inject each unique src exactly once across the drawer's lifetime.
    scripts.forEach((oldScript) => {
      const src = oldScript.getAttribute('src');
      if (!src || this.#revivedScripts.has(src)) return;
      this.#revivedScripts.add(src);

      // ES modules: load via dynamic import() rather than injecting a fresh
      // <script type="module" src>. On Safari < 26 a dynamically-inserted
      // module <script> is added to the DOM but never evaluated, so the
      // module's custom-element registrations (e.g. cart-items-component)
      // silently never run — the drawer's qty/remove controls then no-op
      // because cart-items-component never attaches its document listener,
      // while the server-rendered cart page works (its scripts load at parse
      // time). import() executes the module graph identically across engines,
      // dedupes through the module map, and resolves bare @theme/* imports
      // via the page's import map.
      if (oldScript.getAttribute('type') === 'module') {
        const url = new URL(src, document.baseURI).href;
        import(url).catch((err) => console.error('[cart-drawer] module revive failed:', src, err));
        return;
      }

      // Classic scripts (e.g. cart-discount-celebration.js) must be injected
      // as a fresh <script> so they execute exactly once on insertion.
      const fresh = document.createElement('script');
      ['src', 'type', 'async', 'defer', 'fetchpriority', 'nomodule'].forEach((attr) => {
        const v = oldScript.getAttribute(attr);
        if (v !== null) fresh.setAttribute(attr, v);
      });
      dialog.appendChild(fresh);
    });
  }

  /** @type {Set<string>} */
  #revivedScripts = new Set();

  #handleCartAdd = async () => {
    this.#inflateTemplate();

    // If the ATC sheet is handling this event, don't auto-open the drawer
    if (document.documentElement.dataset.atcSheetActive === '1') {
      // Still re-render the cart contents so they're fresh when the user opens the drawer
      const cartItems = this.querySelector('cart-items-component');
      const sectionId = cartItems?.dataset.sectionId;
      if (sectionId) {
        await sectionRenderer.renderSection(sectionId, { cache: false });
      }
      return;
    }

    if (this.hasAttribute('auto-open')) {
      // Show a loading state immediately so the buyer doesn't see the
      // SSR-cached empty-cart screen while the section re-renders with
      // the just-added line item. The class is cleared in the
      // section:morph handler below.
      const dialog = this.querySelector('dialog');
      dialog?.classList.add('cart-drawer--loading-add');
      this.showDialog();

      // Re-render the cart drawer section to update content
      const cartItems = this.querySelector('cart-items-component');
      const sectionId = cartItems?.dataset.sectionId;
      if (sectionId) {
        try {
          await sectionRenderer.renderSection(sectionId, { cache: false });
        } finally {
          // section:morph fires inflateTemplate which rebuilds the
          // dialog innerHTML — re-query for the new <dialog> before
          // clearing the class.
          this.querySelector('dialog')?.classList.remove('cart-drawer--loading-add');
        }
        // Initialize countdown - function may not exist if inline script hasn't run yet
        if (typeof window.initAllUrgencyCountdowns === 'function') {
          window.initAllUrgencyCountdowns('cart-drawer-post-render');
        } else {
          this.#initCountdowns();
        }
      } else {
        dialog?.classList.remove('cart-drawer--loading-add');
      }

    }
  };

  /**
   * Initialize urgency countdown elements (fallback when global function not available)
   */
  #initCountdowns() {
    document.querySelectorAll('.cart-urgency-countdown').forEach((container) => {
      // Check if this is an LOD countdown
      if (container.classList.contains('cart-urgency-countdown--lod')) {
        this.#updateLODCountdown(container);
        return;
      }

      // Print run countdown
      const timerEl = container.querySelector('.cart-urgency-countdown__timer');
      if (!timerEl) return;

      const now = new Date();
      const cutoff = new Date(now);
      const h = now.getHours();

      if (h < 13) {
        cutoff.setHours(13, 0, 0, 0);
      } else if (h < 22) {
        cutoff.setHours(22, 0, 0, 0);
      } else {
        container.style.display = 'none';
        return;
      }

      if (now >= cutoff) {
        container.style.display = 'none';
        return;
      }

      const diff = cutoff - now;
      const hrs = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      timerEl.textContent = hrs > 0 ? `${hrs} hrs ${String(mins).padStart(2, '0')} mins` : `${mins} mins`;
      container.style.display = '';
    });
  }

  /**
   * Update LOD countdown (fallback when global function not available)
   */
  #updateLODCountdown(container) {
    const lodStandard = container.dataset.lodStandard;
    const lodExpress = container.dataset.lodExpress;
    const messageEl = container.querySelector('.cart-urgency-countdown__lod-message');
    if (!messageEl) return;

    const now = new Date();
    let targetDate = null;
    let state = 'none';

    // Check standard first
    if (lodStandard) {
      const standardDate = new Date(lodStandard);
      if (now < standardDate) {
        targetDate = standardDate;
        state = 'standard';
      }
    }

    // If standard passed, check express
    if (!targetDate && lodExpress) {
      const expressDate = new Date(lodExpress);
      if (now < expressDate) {
        targetDate = expressDate;
        state = 'express';
      }
    }

    if (!targetDate) {
      container.style.display = 'none';
      return;
    }

    container.dataset.lodState = state;
    const diff = targetDate - now;
    const hoursRemaining = diff / (1000 * 60 * 60);

    // Format date for display
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dateStr = `${days[targetDate.getDay()]} ${targetDate.getDate()} ${months[targetDate.getMonth()]}`;

    // Get translation parts from data attributes or use defaults
    const orderBy = container.dataset.tOrderBy || 'Order by';
    const forDelivery = container.dataset.tForDelivery || 'for Christmas delivery';
    const expressNote = container.dataset.tExpressNote || 'Select Express Shipping at checkout.';
    const countdownPrefix = container.dataset.tCountdownPrefix || 'Order within';
    const countdownSuffix = container.dataset.tCountdownSuffix || 'for Christmas delivery';

    let message;
    if (hoursRemaining < 48) {
      // Show countdown
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const timeStr = hours > 0 ? `<strong>${hours}h ${String(minutes).padStart(2, '0')}m</strong>` : `<strong>${minutes}m</strong>`;
      message = `${countdownPrefix} ${timeStr} ${countdownSuffix}`;
      if (state === 'express') {
        message += `<br>${expressNote}`;
      }
    } else {
      // Show date
      message = `${orderBy} <strong>${dateStr}</strong> ${forDelivery}`;
      if (state === 'express') {
        message += `<br>${expressNote}`;
      }
    }

    messageEl.innerHTML = message;
    container.style.display = '';
  }

  open() {
    this.#inflateTemplate();
    this.showDialog();
    document.dispatchEvent(new CustomEvent('cart:drawer-opened'));

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
